import getAMBClient from './getAMBClient';
import {createSubscriptionId} from './utils';
import {get, toUpper} from '@servicenow/ui-internal';
import createGarbageCollectorMap from './createGarbageCollectorMap';

const eventSubscriptionRegistry = createGarbageCollectorMap((subscriptionId, eventIds) => {
	eventIds.forEach(id => getAMBClient().unsubscribeFromEvent(id));
});

export function ambEventEffect(subscriptionId, ambClient, eventSubscriptionRegistry, coeffects) {
	const {action, dispatch, host} = coeffects;
	const shouldSubscribe = get(action, 'payload.subscribe', false);
	if (!eventSubscriptionRegistry.has(subscriptionId) && shouldSubscribe) {
		const eventMap = ambClient.getEvents();
		const eventIds = [];
		for (const key in eventMap) {
			eventIds.push(
				ambClient.subscribeToEvent(eventMap[key], () => dispatch(toUpper(`AMB_EVENT_${key}`)))
			);
		}

		eventSubscriptionRegistry.set(subscriptionId, eventIds, host);
	} else if (eventSubscriptionRegistry.has(subscriptionId) && !shouldSubscribe) {
		eventSubscriptionRegistry.delete(subscriptionId);
	}
}

/**
 * Effect to subscribe to AMB events.
 *
 * Dispatches the following actions when the corresponding AMB event occurs:
 *  * AMB_EVENT_CONNECTION_INITIALIZED
 *  * AMB_EVENT_CONNECTION_OPENED
 *  * AMB_EVENT_CONNECTION_CLOSED
 *  * AMB_EVENT_CONNECTION_BROKEN
 *  * AMB_EVENT_SESSION_LOGGED_IN
 *  * AMB_EVENT_SESSION_LOGGED_OUT
 *  * AMB_EVENT_SESSION_INVALIDATED
 *
 * @function createAmbEventEffect
 * @example
 * import {createCustomElement} from '@servicenow/ui-core';
 * import {createAmbEventEffect} from '@servicenow/ui-effect-amb';
 *
 * import {
 *  AMB_EVENT_SUBSCRIPTION_CHANGED,
 *  AMB_EVENT_SESSION_LOGGED_IN,
 *  AMB_EVENT_SESSION_LOGGED_OUT
 * } from './actions';
 *
 * createCustomElement('user-status', {
 *  renderer: {
 *      view(state) {
 *          if(state.loggedIn)
 *              return <h1>Logged In</h1>
 *          else
 *              return <h1>Logged Out</h1>
 *      }
 *  },
 *  actionHandlers: {
 *      [COMPONENT_CONNECTED]: () => {
 *          dispatch(AMB_EVENT_SUBSCRIPTION_CHANGED, {subscribe: true});
 *      },
 *      [COMPONENT_DISCONNECTED]: () => {
 *          dispatch(AMB_EVENT_SUBSCRIPTION_CHANGED, {subscribe: false});
 *      },
 *      [AMB_EVENT_SUBSCRIPTION_CHANGED]: createAmbEventEffect(),
 *      [AMB_EVENT_SESSION_LOGGED_IN]: ({updateState}) => updateState({loggedIn: true}),
 *      [AMB_EVENT_SESSION_LOGGED_OUT]: ({updateState}) => updateState({loggedIn: false})
 *  }
 * });
 *
 * @return {Object} AMB Event Effect Descriptor
 */
export default function createAmbEventEffect() {
	return {
		effect: ambEventEffect,
		args: [createSubscriptionId(), getAMBClient(), eventSubscriptionRegistry]
	};
}
